import React, { useState, useRef, useEffect } from 'react'
import { useImageLoading } from '../hooks/useImageLoading'
import deleteImg from '../../../assets/images/delete2.svg'

// A single tile in a category carousel,
// displaying a single thumbnail of an asset representative of the category.
const CategoryThumbnail = ({ elm, subCategoryIdx, onThumbClick, onEditName, onDeleteCategory, onEditDocumentName, onDeleteDocument, placeholderOverride }) => {
  const { getImageStyles, createOnLoadHandler, createOnErrorHandler } =
    useImageLoading('navigation')

  const [isEditing, setIsEditing] = useState(false)
  const [editName, setEditName] = useState(elm.name)
  const inputRef = useRef(null)

  const isKinCategory = !!elm.userId
  const isDocument = elm.type === 'document'
  const showActions = isKinCategory || isDocument

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleClick = (event) => {
    if (event && typeof event.stopPropagation === 'function') {
      event.stopPropagation()
      event.preventDefault()
    }

    if (typeof onThumbClick === 'function') {
      onThumbClick(elm)
      return
    }

    if (elm.type === 'document') {
      if (elm.fileUrl) window.open(elm.fileUrl, '_blank')
    }
  }

  const handleEditClick = (event) => {
    event.stopPropagation()
    event.preventDefault()
    setEditName(elm.name)
    setIsEditing(true)
  }

  const handleDeleteClick = (event) => {
    event.stopPropagation()
    event.preventDefault()
    if (isDocument && typeof onDeleteDocument === 'function') {
      onDeleteDocument(elm)
    } else if (typeof onDeleteCategory === 'function') {
      onDeleteCategory(elm)
    }
  }

  const handleSave = () => {
    const trimmed = editName.trim()
    if (trimmed && trimmed !== elm.name) {
      if (isDocument && typeof onEditDocumentName === 'function') {
        onEditDocumentName(elm, trimmed)
      } else if (typeof onEditName === 'function') {
        onEditName(elm, trimmed)
      }
    }
    setIsEditing(false)
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleSave()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      setEditName(elm.name)
      setIsEditing(false)
    }
  }

  const hasThumbnail = !!(elm?.thumbnailUrl)
  const thumbnailStyle = hasThumbnail
    ? { ...getImageStyles(elm.id, elm?.thumbnailUrl) }
    : placeholderOverride
    ? {
        backgroundImage: `url('${placeholderOverride}')`,
        backgroundSize: '60% 60%',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#ffffff'
      }
    : { ...getImageStyles(elm.id, elm?.thumbnailUrl) }

  return (
    <div
      className="center-contents-block"
      key={'subCategoryIdx_' + subCategoryIdx}
    >
      <div
        className="thumb"
        id={`thumb-${subCategoryIdx}`}
        onClick={handleClick}
        style={thumbnailStyle}
      >
        {hasThumbnail && (
          <img
            src={elm?.thumbnailUrl}
            alt=""
            onLoad={createOnLoadHandler(elm.id)}
            onError={createOnErrorHandler(elm.id)}
            style={{ display: 'none' }}
          />
        )}
        {showActions && (onEditName || onEditDocumentName) && (
          <button
            className="category-edit-icon"
            onClick={handleEditClick}
            title={isDocument ? "Rename document" : "Rename category"}
          >
            ✎
          </button>
        )}
        {showActions && (onDeleteCategory || onDeleteDocument) && (
          <button
            className="category-delete-icon"
            onClick={handleDeleteClick}
            title={isDocument ? "Delete document" : "Delete category"}
          >
            <img src={deleteImg} alt="Delete" style={{ width: '16px', height: '16px' }} />
          </button>
        )}
      </div>
      <span>
        {isEditing ? (
          <input
            ref={inputRef}
            className="thumb-title-input"
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
          />
        ) : (
          <button className="thumb-title">{elm.name}</button>
        )}
      </span>
    </div>
  )
}

export default CategoryThumbnail
